import { Component, computed, effect, inject, OnDestroy } from '@angular/core';
import { FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { JSONContent } from '@tiptap/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormInputComponent, FormNumberComponent, FormWysiwygComponent, FormToggleSwitchComponent, ErrorComponent } from '@shared/forms';
import { MaterialListComponent, MediaVideoComponent, MediaAudioComponent, MediaImageComponent, MediaTextComponent } from '@shared/components';
import { LessonFacade, LessonValue } from '@app/store/lesson';
import { FormOptionsFacade } from '@app/store/form-options';
import { LessonType, MediaType } from '@app/store/shared/models';
import { MediaSelectionService, MediaTarget } from '@app/dialogs';

@Component({
    selector: 'app-add-form',
    templateUrl: './add-form.component.html',
    styleUrls: ['./add-form.component.css'],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        RouterLink,
        ButtonModule,
        FormInputComponent,
        FormNumberComponent,
        FormWysiwygComponent,
        FormToggleSwitchComponent,
        ErrorComponent,
        MediaVideoComponent,
        MediaAudioComponent,
        MediaImageComponent,
        MediaTextComponent,
        MaterialListComponent,
    ],
})
export class AddFormComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly lessonFacade = inject(LessonFacade);
    private readonly formOptionsFacade = inject(FormOptionsFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly translateService = inject(TranslateService);
    private readonly mediaSelectionService = inject(MediaSelectionService);

    readonly type = <LessonType>(this.route.snapshot.params['type'] ?? 'video');
    readonly saving = this.lessonFacade.saving;

    readonly title = computed(() => {
        const typeLabel = this.translateService.instant(`lessons.type.${this.type}`);
        return this.translateService.instant('lessons.modal.add.typeTitle', { type: typeLabel });
    });

    readonly form = this.formBuilder.group({
        name: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        duration: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
        // Video/Audio/Text specific
        mediaId: new FormControl<string | null>(null),
        mediaPlaceholderId: new FormControl<string | null>(null),
        transcription: new FormControl<JSONContent | null>(null),
        // Video specific
        completionEnforcement: this.formBuilder.control(false),
        // Materials (PDF)
        materialIds: this.formBuilder.control<string[]>([]),
    });

    constructor() {
        this.initValidators();

        // Close on save success
        effect(() => {
            if (this.lessonFacade.saved()) {
                this.close();
            }
        });

        // Error handling - re-enable form on error
        effect(() => {
            if (this.lessonFacade.saving() === false && this.lessonFacade.saved() === false) {
                this.form.enable();
            }
        });

        // Listen for media selection from dialog
        effect(() => {
            const selection = this.mediaSelectionService.selected();
            if (!selection) return;

            if (selection.target === 'materialIds') {
                const current = this.form.controls.materialIds.value ?? [];
                const id = selection.media.id;
                if (!current.includes(id)) {
                    this.form.controls.materialIds.setValue([...current, id]);
                }
            } else if (selection.target === 'mediaId') {
                this.form.controls.mediaId.setValue(selection.media.id);
            } else if (selection.target === 'mediaPlaceholderId') {
                this.form.controls.mediaPlaceholderId.setValue(selection.media.id);
            } else {
                return;
            }

            this.mediaSelectionService.clear();
        });
    }

    ngOnDestroy(): void {
        this.lessonFacade.clear();
        this.formOptionsFacade.clear();
    }

    updateDuration(duration: number): void {
        this.form.controls.duration.setValue(duration);
    }

    onSelectMedia(target: MediaTarget, mediaType: MediaType): void {
        this.router.navigate([{ outlets: { dialog: ['select-media', { mediaTarget: target, mediaType }] } }]);
    }

    removeMaterial(id: string): void {
        const current = this.form.controls.materialIds.value ?? [];
        this.form.controls.materialIds.setValue(current.filter((mid) => mid !== id));
    }

    reorderMaterials(ids: string[]): void {
        this.form.controls.materialIds.setValue(ids);
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        if (this.form.invalid) {
            return;
        }

        this.form.disable();
        const value = this.form.getRawValue();

        let lessonValue: LessonValue;

        if (this.type === 'video') {
            lessonValue = {
                type: 'video',
                name: value.name,
                duration: value.duration ?? 0,
                transcription: value.transcription || undefined,
                mediaId: value.mediaId ?? undefined,
                mediaPlaceholderId: value.mediaPlaceholderId ?? undefined,
                completionEnforcement: value.completionEnforcement,
                materialIds: value.materialIds,
            };
        } else if (this.type === 'audio') {
            lessonValue = {
                type: 'audio',
                name: value.name,
                duration: value.duration ?? 0,
                transcription: value.transcription || undefined,
                mediaId: value.mediaId ?? undefined,
                materialIds: value.materialIds,
            };
        } else {
            lessonValue = {
                type: 'text',
                name: value.name,
                duration: value.duration ?? 0,
                mediaId: value.mediaId ?? undefined,
                materialIds: value.materialIds,
            };
        }

        this.lessonFacade.createLesson(lessonValue);
    }

    close(): void {
        const outlet = this.route.parent?.outlet ?? 'modal';
        this.router.navigate([{ outlets: { [outlet]: null } }]);
    }

    private initValidators(): void {
        if (this.type === 'video') {
            this.form.controls.mediaId.setValidators([Validators.required]);
            this.form.controls.mediaPlaceholderId.setValidators([Validators.required]);
        } else if (this.type === 'audio' || this.type === 'text') {
            this.form.controls.mediaId.setValidators([Validators.required]);
        }

        this.form.controls.mediaId.updateValueAndValidity();
        this.form.controls.mediaPlaceholderId.updateValueAndValidity();
    }
}
